/**
 * ObsidianWeb — tab bar component (Task 5).
 *
 * Tracks a stack of open files. Clicking a file in the FileTree opens (or
 * focuses, if already open) a tab; clicking a tab switches active; clicking
 * the × button closes it. State is pure; the parent Shell owns the tab list
 * and threads callbacks down.
 *
 * The helpers (`openTab`, `closeTab`, `switchTab`, `tabLabel`) are exported
 * for unit tests — the smoke runs them headless without rendering.
 */

import type { TreeNode } from '../api.js';

export interface Tab {
  readonly path: string;
  readonly title: string;
}

export interface TabBarState {
  readonly tabs: readonly Tab[];
  readonly activePath: string | null;
}

/** Display name for a tab — last path segment, stripped of `.md`. */
export function tabLabel(filePath: string): string {
  const segments = filePath.split('/').filter((s) => s.length > 0);
  const last = segments[segments.length - 1] ?? filePath;
  return last.replace(/\.md$/i, '');
}

/**
 * Open `filePath` as a new tab, or focus the existing tab if already open.
 * Always sets `activePath` to the opened file.
 */
export function openTab(state: TabBarState, filePath: string): TabBarState {
  const existing = state.tabs.find((t) => t.path === filePath);
  if (existing) return { tabs: state.tabs, activePath: filePath };
  const next: Tab = { path: filePath, title: tabLabel(filePath) };
  return { tabs: [...state.tabs, next], activePath: filePath };
}

/**
 * Close the tab at `filePath`. If it was active, activate its right
 * neighbour (or left if none), or null when no tabs remain.
 */
export function closeTab(state: TabBarState, filePath: string): TabBarState {
  const idx = state.tabs.findIndex((t) => t.path === filePath);
  if (idx === -1) return state;
  const tabs = state.tabs.filter((t) => t.path !== filePath);
  if (state.activePath !== filePath) {
    return { tabs, activePath: state.activePath };
  }
  if (tabs.length === 0) return { tabs, activePath: null };
  const nextIdx = idx < tabs.length ? idx : tabs.length - 1;
  const nextTab = tabs[nextIdx];
  return { tabs, activePath: nextTab ? nextTab.path : null };
}

/** Switch the active tab. No-op if the path is not currently open. */
export function switchTab(state: TabBarState, filePath: string): TabBarState {
  if (!state.tabs.some((t) => t.path === filePath)) return state;
  return { tabs: state.tabs, activePath: filePath };
}

/**
 * Build a tab from a tree node — convenience used by Shell when a file is
 * picked from the FileTree. Exported for the smoke test.
 */
export function tabFromNode(node: TreeNode): Tab {
  return { path: node.path, title: tabLabel(node.path) };
}

export interface TabBarProps {
  readonly tabs: readonly Tab[];
  readonly activePath: string | null;
  readonly onSwitch: (filePath: string) => void;
  readonly onClose: (filePath: string) => void;
}

export function TabBar(props: TabBarProps): JSX.Element {
  if (props.tabs.length === 0) {
    return (
      <div className="ow-tab-bar" role="tablist" aria-label="Open files">
        <span className="ow-tab-empty">No file open — pick one from the tree.</span>
      </div>
    );
  }
  return (
    <div className="ow-tab-bar" role="tablist" aria-label="Open files">
      {props.tabs.map((tab) => {
        const isActive = tab.path === props.activePath;
        return (
          <button
            key={tab.path}
            type="button"
            role="tab"
            aria-selected={isActive}
            className={`ow-tab${isActive ? ' active' : ''}`}
            onClick={() => props.onSwitch(tab.path)}
            title={tab.path}
            data-testid={`tab-${tab.path}`}
          >
            <span className="ow-tab-label">{tab.title}</span>
            <span
              className="ow-tab-close"
              role="button"
              aria-label={`Close ${tab.title}`}
              onClick={(e) => {
                e.stopPropagation();
                props.onClose(tab.path);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.stopPropagation();
                  e.preventDefault();
                  props.onClose(tab.path);
                }
              }}
              tabIndex={0}
            >
              ×
            </span>
          </button>
        );
      })}
    </div>
  );
}

export default TabBar;
